var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios, { isAxiosError } from 'axios';
import { config } from 'lib-shared-backend-config';
import { electron } from 'lib-shared-electron';
import { compareSemVer } from 'lib-shared-semver';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { parseAccessToken } from '../jwt';
import { noTokenRedirect } from '../redirect';
import { authStorage } from './authStorage';
import { addErrorInterceptor } from './interceptor';
/** The axios instance to use. */
const axiosInstance = axios.create();
addErrorInterceptor(axiosInstance);
/* @TODO Replace once we have debug util in a module or we choose to use the 'debug' package */
const debugLog = console.log;
function sleep(seconds) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
    });
}
export class AuthInitError extends Error {
    constructor(message, options) {
        super(message, options);
    }
}
/**
 * Generates a random retry delay in seconds based on the number of tries. This
 * is used to prevent thundering herds.
 *
 * @param tryCount - The number of tries.
 * @returns The retry delay in seconds.
 */
function generateRetryDelayInSeconds(tryCount = 0) {
    const timeout = Math.min(Math.round((Math.random() + 1) * Math.pow(1.5, tryCount)), 30);
    return timeout;
}
const defaultRetries = 5;
/**
 * Represents the auth setup (access/refresh tokens). Letting consumers get auth
 * tokens without exposing the implementation details and the need to refresh
 * tokens.
 *
 * This encapsulates the logic of infinitely refreshing of the access-token
 * using the refresh-token.
 *
 * - The access-token expires every 10 minutes.
 * - The refresh-token expires every 7 days
 *
 * When the access-token expires or is about to expire, the refresh-token is
 * used to "refresh" both tokens.
 */
class AuthManager {
    constructor() {
        Object.defineProperty(this, "authDataPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "retryCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    init(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const authData = yield this.refresh(refreshToken);
            if (authData) {
                authStorage.setAuthData(authData);
            }
            else {
                const failureReason = authStorage.isRefreshTokenExpired(refreshToken)
                    ? 'expired'
                    : 'unknown';
                throw new AuthInitError('Failed to init refresh token', {
                    cause: failureReason,
                });
            }
        });
    }
    getDebugInfo() {
        const accessToken = authStorage.getAccessToken();
        if (!accessToken)
            return {};
        const prevAccessTokenPayload = parseAccessToken(accessToken);
        const { exp } = prevAccessTokenPayload;
        return {
            expire: exp ? new Date(exp * 1000).toISOString() : null,
            now: new Date().toISOString(),
        };
    }
    /**
     * Gets auth data from LocalStorage, or fetches new auth data if the current
     * token is expired or is about to expire.
     */
    getAuthData() {
        return __awaiter(this, arguments, void 0, function* ({ localStorageOnly = false, } = {}) {
            if (localStorageOnly) {
                return authStorage.getAuthData();
            }
            // If another request is already in progress, use its result
            if (this.authDataPromise) {
                return yield this.authDataPromise;
            }
            const currentAuthData = authStorage.getAuthData();
            if (!currentAuthData) {
                return null;
            }
            if (authStorage.isAccessTokenExpired()) {
                // If the token is expired or about to expire, we want to refresh it
                Sentry.addBreadcrumb({
                    category: 'refreshToken',
                    // Cannot include "token" in the message, as it will be redacted
                    message: 'expired or about to expire',
                    level: 'debug',
                    data: this.getDebugInfo(),
                });
                // In case multiple requests are made at the same time, we only want to
                // refresh the token once, and have all requests use the same refreshed
                // token. So we store the promise in this.authDataPromise, and have all
                // requests use it.
                this.authDataPromise = this.refresh(currentAuthData.refresh_token).then((newAuthData) => {
                    if (newAuthData) {
                        authStorage.setAuthData(newAuthData);
                    }
                    // Clear the promise so that the next call to getAuth will check expiry/fetch again
                    this.authDataPromise = null;
                    return newAuthData;
                });
                return yield this.authDataPromise;
            }
            return currentAuthData;
        });
    }
    removeAuthData() {
        return authStorage.removeAuthData();
    }
    validateAuthDataComplete(authData) {
        return authStorage.validateAuthDataComplete(authData);
    }
    isAccessTokenExpired() {
        return authStorage.isAccessTokenExpired();
    }
    isRefreshTokenExpired() {
        const auth = authStorage.getAuthData();
        if (!auth)
            return true;
        return authStorage.isRefreshTokenExpired(auth.refresh_token);
    }
    /** Checks whether refresh token is supported. */
    isSupported() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!electron.isElectron()) {
                return true;
            }
            const version = yield electron.getVersion();
            return compareSemVer(version, '3.7.0') === 1;
        });
    }
    /**
     * Gets refreshed tokens from the API
     *
     * @param refreshToken The refresh token.
     * @param retriesLeft The number of retries left.
     */
    refresh(refreshToken_1) {
        return __awaiter(this, arguments, void 0, function* (refreshToken, retriesLeft = defaultRetries) {
            try {
                return yield axiosInstance
                    .post(`${config.url.rest}/v2/login/refresh`, {
                    refreshToken,
                })
                    .then((response) => {
                    this.retryCount = 0;
                    return response.data.data;
                });
            }
            catch (error) {
                return yield this.handleRefreshError(error, refreshToken, retriesLeft);
            }
        });
    }
    waitUntilOnline() {
        return __awaiter(this, void 0, void 0, function* () {
            const networkWasOffline = navigator.onLine === false;
            if (networkWasOffline) {
                debugLog('AUTH', 'Network connection offline - waiting...');
                Sentry.addBreadcrumb({
                    category: 'refreshToken',
                    message: 'Network connection offline - waiting...',
                    level: 'debug',
                });
            }
            while (navigator.onLine === false) {
                yield sleep(5);
            }
            if (networkWasOffline) {
                debugLog('AUTH', 'Network connection restored');
                Sentry.addBreadcrumb({
                    category: 'refreshToken',
                    message: 'Network connection restored',
                    level: 'debug',
                });
            }
        });
    }
    retryRefresh(refreshToken, retriesLeft) {
        return __awaiter(this, void 0, void 0, function* () {
            // If the network is offline, we want to wait for it to come back online
            yield this.waitUntilOnline();
            this.retryCount++;
            const delay = generateRetryDelayInSeconds(this.retryCount);
            debugLog('AUTH', `Retrying in ${delay} seconds`);
            yield sleep(delay);
            return yield this.refresh(refreshToken, retriesLeft);
        });
    }
    handleRefreshError(error, refreshToken, retriesLeft) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (authStorage.isRefreshTokenExpired(refreshToken)) {
                debugLog('AUTH', 'Refresh token expired');
                Sentry.addBreadcrumb({
                    category: 'refreshToken',
                    message: 'Refresh token expired',
                    level: 'debug',
                });
                authStorage.removeAuthData();
                noTokenRedirect();
                return null;
            }
            if (isAxiosError(error)) {
                if (error.message === 'timeout exceeded') {
                    debugLog('AUTH', 'Timeout exceeded');
                    Sentry.addBreadcrumb({
                        category: 'refreshToken',
                        message: 'Timeout exceeded',
                        level: 'debug',
                    });
                    return yield this.retryRefresh(refreshToken, retriesLeft);
                }
                if (error.message === 'Network Error') {
                    debugLog('AUTH', 'Network error');
                    Sentry.addBreadcrumb({
                        category: 'refreshToken',
                        message: 'Network error',
                        level: 'debug',
                    });
                    return yield this.retryRefresh(refreshToken, retriesLeft);
                }
                if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                    // If the refresh token is invalid, we want to remove it from localStorage
                    debugLog('AUTH', 'Refresh token invalid (401)');
                    authStorage.removeAuthData();
                    noTokenRedirect();
                    return null;
                }
                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        team: SentryTeam.Platform,
                        errorSource: 'authManager.refresh',
                    },
                });
            }
            if (retriesLeft <= 0) {
                debugLog('AUTH', 'Retries exhausted, logging out');
                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        team: SentryTeam.Platform,
                        errorSource: 'authManager.refresh',
                    },
                });
                authStorage.removeAuthData();
                noTokenRedirect();
                return null;
            }
            debugLog('AUTH', `Retrying ${retriesLeft} more times`);
            return yield this.retryRefresh(refreshToken, retriesLeft - 1);
        });
    }
    accessTokenExpiresInMs() {
        return authStorage.accessTokenExpiresInMs();
    }
}
export const authManager = new AuthManager();
//# sourceMappingURL=authManager.js.map